import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Building2, Calendar, Clock, Play, AlertCircle, Briefcase, Target, User } from 'lucide-react';
import { useMediaDevices } from '../../hooks/useMediaDevices';
import CameraPreview from './CameraPreview';
import DeviceControls from './DeviceControls';
import { formatDistanceToNow } from 'date-fns';
import { Navbar } from '../Navbar/Navbar';
import { Button } from '../common/Button';

interface PublicInterviewPreviewProps {
  companyName: string;
  companyPhoto?: string;
  candidateName: string;
  candidatePhoto?: string;
  interviewTitle?: string;
  interviewPosition?: string;
  onStartInterview: () => void;
  expiresAt: string;
}

const PublicInterviewPreview: React.FC<PublicInterviewPreviewProps> = ({
  companyName,
  companyPhoto,
  candidateName,
  candidatePhoto,
  interviewTitle,
  onStartInterview,
  interviewPosition,
  expiresAt,
}) => {
  const [isDeviceDropdownOpen, setIsDeviceDropdownOpen] = useState<boolean>(false);
  const [isTogglingCamera, setIsTogglingCamera] = useState<boolean>(false);
  const [isTogglingMic, setIsTogglingMic] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deviceError, setDeviceError] = useState<{
    hasError: boolean;
    message: string;
    isBlocking: boolean;
  }>({
    hasError: false,
    message: '',
    isBlocking: false,
  });

  const errorDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMediaError = useCallback((error: Error) => {
    console.error('Media devices error:', error);

    if (errorDebounceTimeoutRef.current) {
      clearTimeout(errorDebounceTimeoutRef.current);
    }

    errorDebounceTimeoutRef.current = setTimeout(() => {
      if (error.name === 'NotFoundError') {
        setDeviceError({
          hasError: true,
          message: 'No camera or microphone found. Please connect a device and try again.',
          isBlocking: true,
        });
      } else if (error.name === 'NotAllowedError') {
        setDeviceError({
          hasError: true,
          message: 'Permission to use camera or microphone was denied. Please enable permissions in your browser settings.',
          isBlocking: true,
        });
      } else {
        setDeviceError({
          hasError: true,
          message: `Error accessing media devices: ${error.message || error.name}`,
          isBlocking: true,
        });
      }
    }, 500);
  }, []);

  const mediaDevices = useMediaDevices({
    initialCameraEnabled: true,
    initialMicEnabled: true,
    onError: handleMediaError,
  });

  const {
    videoRef,
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    isCameraEnabled,
    isMicEnabled,
    hasCameraPermission,
    hasMicPermission,
    toggleCamera,
    toggleMicrophone,
    handleDeviceChange,
    stream,
  } = mediaDevices;

  const handleToggleCamera = async () => {
    setIsTogglingCamera(true);
    await toggleCamera();
    setIsTogglingCamera(false);
  };

  const handleToggleMicrophone = async () => {
    setIsTogglingMic(true);
    await toggleMicrophone();
    setIsTogglingMic(false);
  };

  const toggleDeviceDropdown = () => {
    setIsDeviceDropdownOpen(!isDeviceDropdownOpen);
  };

  // Update document title with interview title
  useEffect(() => {
    const originalTitle = document.title;
    if (interviewTitle) {
      document.title = interviewTitle;
    } else {
      document.title = 'Interview Preview - MockTrail';
    }

    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, [interviewTitle]);

  const handleCancel = () => {
    // Go back in browser history (close this tab/page)
    window.history.back();
  };

  const handleStartInterviewClick = () => {
    setIsLoading(true);
    onStartInterview();
  };

  const isStartDisabled = () => {
    // Disable if there's a blocking error
    if (deviceError.hasError && deviceError.isBlocking) return true;
    
    // Disable if camera permission is not granted or camera is not enabled
    if (hasCameraPermission !== true || isCameraEnabled !== true) return true;
    
    // Disable if microphone permission is not granted or mic is not enabled
    if (hasMicPermission !== true || isMicEnabled !== true) return true;
    
    return false;
  };

  return (
    <div className="fixed inset-0 bg-background-light dark:bg-background-dark z-50 flex flex-col overflow-auto md:overflow-auto scrollbar-hide-mobile">
      {/* Animated Gradient Background */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-light/5 via-background-light to-secondary-light/5 dark:from-primary-dark/10 dark:via-background-dark dark:to-secondary-dark/10 animate-gradient-slow"></div>

        {/* Particle background effect */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary-light/20 dark:bg-primary-dark/20 rounded-full filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-secondary-light/20 dark:bg-secondary-dark/20 rounded-full filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-tertiary-light/20 dark:bg-tertiary-dark/20 rounded-full filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>
      </div>

      {/* Fixed Navbar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar
          showUserActions={false}
          showLogoText={true}
        />
      </div>

      {/* Main content wrapper */}
      <div className="flex flex-col min-h-screen pt-20 md:pt-24 pb-8 md:justify-center">
        {/* Main content */}
        <div className="relative z-10 max-w-7xl w-full flex flex-col md:flex-row gap-4 md:gap-6 px-4 md:px-8 mx-auto">
          {/* Left side - Camera preview */}
          <div className="w-full md:w-3/5 h-[40vh] md:h-[60vh] flex items-center justify-center">
            <CameraPreview
              videoRef={videoRef}
              isCameraEnabled={isCameraEnabled}
              profilePicture={candidatePhoto}
              userName={candidateName}
              stream={stream}
              errorState={deviceError.hasError}
            >
              <DeviceControls
                isCameraEnabled={isCameraEnabled}
                isMicEnabled={isMicEnabled}
                toggleCamera={handleToggleCamera}
                toggleMicrophone={handleToggleMicrophone}
                isDeviceDropdownOpen={isDeviceDropdownOpen}
                toggleDeviceDropdown={toggleDeviceDropdown}
                videoDevices={videoDevices}
                audioDevices={audioDevices}
                selectedVideoDevice={selectedVideoDevice}
                selectedAudioDevice={selectedAudioDevice}
                handleDeviceChange={handleDeviceChange}
                isTogglingCamera={isTogglingCamera}
                isTogglingMic={isTogglingMic}
                disableMicToggle={false}
              />
            </CameraPreview>
          </div>

          {/* Right side - Interview details */}
          <div className="w-full md:w-2/5 h-auto md:h-[60vh] rounded-xl border border-border-light dark:border-border-dark bg-surface-light/90 dark:bg-surface-dark/90 backdrop-blur-md overflow-hidden animate-navbar-item-fade-in mt-4 md:mt-0">
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-y-visible md:overflow-y-auto scrollbar-hide-mobile px-4 pt-4 md:px-6 md:pt-6 pb-2">
                <h1 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-1">
                  Interview Preview
                </h1>
                <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4 text-sm">
                  Prepare yourself and check your settings before starting
                </p>

                {/* Interview Details Card */}
                <div className="bg-background-light/70 dark:bg-background-dark/70 rounded-xl p-4 border border-border-light dark:border-border-dark mb-4">
                  {/* Company Info */}
                  <div className="flex items-center gap-3 mb-4">
                    {companyPhoto ? (
                      <img
                        src={companyPhoto}
                        alt={companyName}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-primary-light dark:bg-primary-dark flex items-center justify-center">
                        <span className="text-white text-xl font-bold font-heading">
                          {companyName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Company</p>
                      <p className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                        {companyName}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {/* Candidate Name */}
                    <div className="flex items-center text-sm">
                      <User className="w-4 h-4 mr-2 text-primary-light dark:text-primary-dark flex-shrink-0" />
                      <div>
                        <span className="text-text-secondary-light dark:text-text-secondary-dark">Candidate: </span>
                        <span className="font-medium text-text-primary-light dark:text-text-primary-dark">
                          {candidateName}
                        </span>
                      </div>
                    </div>

                    {/* Interview Title */}
                    {interviewTitle && (
                      <div className="flex items-center text-sm">
                        <Building2 className="w-4 h-4 mr-2 text-primary-light dark:text-primary-dark flex-shrink-0" />
                        <div>
                          <span className="text-text-secondary-light dark:text-text-secondary-dark">Title: </span>
                          <span className="font-medium text-text-primary-light dark:text-text-primary-dark">
                            {interviewTitle}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Interview Position */}
                    {interviewPosition && (
                      <div className="flex items-center text-sm">
                        <Briefcase className="w-4 h-4 mr-2 text-primary-light dark:text-primary-dark flex-shrink-0" />
                        <div>
                          <span className="text-text-secondary-light dark:text-text-secondary-dark">Position: </span>
                          <span className="font-medium text-text-primary-light dark:text-text-primary-dark">
                            {interviewPosition}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-primary-light/10 dark:bg-primary-dark/10 border border-primary-light/20 dark:border-primary-dark/20 rounded-lg p-4 mb-4">
                  <p className="text-sm text-text-primary-light dark:text-text-primary-dark">
                    💡 Make sure your camera and microphone are working properly before starting the interview.
                  </p>
                </div>

                {/* Error Message */}
                {deviceError.hasError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                      {deviceError.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons - Below main content, matching InterviewPreviewContainer */}
        <div className="relative z-10 mt-6 mb-8 md:mb-0 max-w-7xl w-full px-4 md:px-8 mx-auto">
          {isStartDisabled() && (
            <div className="flex items-center justify-center md:justify-end text-text-secondary-light dark:text-text-secondary-dark text-sm mb-3">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>Please enable your camera and microphone to start the interview.</span>
            </div>
          )}
          <div className="flex flex-col-reverse md:flex-row justify-center md:justify-end gap-4 mb-8">
            <Button 
              variant="outline" 
              onClick={handleCancel}
              size="md"
              disabled={isLoading}
              className="w-full md:w-auto px-8 transition-all duration-300 hover:bg-background-light dark:hover:bg-background-dark"
            >
              Cancel
            </Button>
            
            <Button 
              variant="primary" 
              size="md"
              leftIcon={<Play className="w-4 h-4" />}
              onClick={handleStartInterviewClick}
              disabled={isLoading || isStartDisabled()}
              className="w-full md:w-auto px-8 py-3.5 transition-all duration-300 hover:scale-105"
            >
              {isLoading ? 'Preparing...' : 'Start Interview'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicInterviewPreview;
